import { AppstoreAddOutlined, BarChartOutlined, BorderOutlined, BuildOutlined, BulbOutlined, CloseCircleOutlined, CloudUploadOutlined, ClusterOutlined, CopyOutlined, FolderOpenOutlined, InboxOutlined, PictureOutlined, ProfileOutlined, ScanOutlined, ShopOutlined, SnippetsOutlined, UserOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Dropdown, Layout, Menu, Select, theme } from "antd";
import Avatar from "antd/es/avatar/Avatar";
import { Header } from "antd/es/layout/layout";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import "./AdminApp.scss";
import { AuthContext } from "./contexts/AuthContext";
import { ConfigContext } from "./contexts/ConfigContext";
import { ClientDTO } from "./openapi/requests";
const { Content, Sider } = Layout;

type MenuItem = {
  key: string;
  url: string | undefined;
  icon: JSX.Element | undefined;
  label: string;
  type?: "submenu" | "group" | undefined;
};

const AdminApp: React.FC = () => {
  const { hasRole, hasPermission, logOut, username } = useContext(AuthContext);
  const { availableClients, currentClient, setCurrentClient } = useContext(ConfigContext);
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('theme') == null ?
    window.matchMedia("(prefers-color-scheme: dark)").matches :
    localStorage.getItem('theme') == "dark");
  const [selectedExportClient, setSelectedExportClient] = useState<ClientDTO>();

  const handleToogleDarkMode = useCallback(() => {
    const darkMode = !isDarkMode;
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    setIsDarkMode(darkMode);
  }, []);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const items: MenuItem[] = useMemo(() => {
    const menuItems: MenuItem[] = [];

    if (hasRole("Cadesign")) {
      menuItems.push({
        type: "group",
        label: "Client",
        url: undefined,
        key: "clientgroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <ShopOutlined />,
        url: "/client",
        key: "client",
        label: "Client",
      });
      menuItems.push({
        type: "group",
        label: "Assets",
        url: undefined,
        key: "assetsgroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <BorderOutlined />,
        url: "/materials",
        key: "materials",
        label: "Materials",
      });
      menuItems.push({
        icon: <BuildOutlined />,
        url: "/models",
        key: "models",
        label: "Models",
      });
      menuItems.push({
        icon: <FolderOpenOutlined />,
        url: "/modelpacks",
        key: "modelpacks",
        label: "Modelpacks",
      });
      menuItems.push({
        icon: <ClusterOutlined />,
        url: "/bommodels",
        key: "boms",
        label: "BOMs",
      });
      menuItems.push({
        icon: <ScanOutlined />,
        url: "/modifiers",
        key: "modifiers",
        label: "Modifiers",
      });
      menuItems.push({
        icon: <PictureOutlined />,
        url: "/scenes",
        key: "scenes",
        label: "Scenes",
      });
      menuItems.push({
        icon: <SnippetsOutlined />,
        url: "/templates",
        key: "templates",
        label: "Templates",
      });
      menuItems.push({
        icon: <SnippetsOutlined />,
        url: "/templatePresets",
        key: "templatepresets",
        label: "Presets",
      });
      menuItems.push({
        icon: <CloudUploadOutlined />,
        url: "/damtransfers",
        key: "damtransfers",
        label: "DAM transfers",
      });
      menuItems.push({
        icon: <AppstoreAddOutlined />,
        url: "/assetprojects",
        key: "assetprojects",
        label: "Asset projects",
      });
      menuItems.push({
        type: "group",
        label: "Orders",
        url: undefined,
        key: "ordersGroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <InboxOutlined />,
        url: '/orders',
        label: 'Orders',
        key: 'orders'
      });
      menuItems.push({
        type: "group",
        label: "UI",
        url: undefined,
        key: "ui",
        icon: undefined,
      });
      menuItems.push({
        icon: <ProfileOutlined />,
        url: "/uiassetfiltergroups",
        key: "uiassetfiltergroups",
        label: "AssetFilter Groups",
      });
      // menuItems.push({
      //   icon: <CloudUploadOutlined />,
      //   url: "/uiassetfilters",
      //   key: "uiassetfilters",
      //   label: "AssetFilters",
      // });
      menuItems.push({
        type: "group",
        label: "Delivery",
        url: undefined,
        key: "deliverygroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <CloudUploadOutlined />,
        url: "/deliveryConfigurations",
        key: "deliveryconfigurations",
        label: "Delivery configurations",
      });
      menuItems.push({
        type: "group",
        label: "Content",
        url: undefined,
        key: "contentgroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <CopyOutlined />,
        url: "/pages",
        key: "pages",
        label: "Pages",
      });
    }

    if (hasPermission("UserAdmin")) {
      menuItems.push({
        type: "group",
        label: "Security",
        url: undefined,
        key: "securitygroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <UserOutlined />,
        url: "/users",
        key: "users",
        label: "Users",
      });
      menuItems.push({
        icon: <UserOutlined />,
        url: "/usergroups",
        key: "usergroups",
        label: "User Groups",
      });
    }

    if (hasPermission("ViewStatistics")) {
      menuItems.push({
        type: "group",
        label: "Info",
        url: undefined,
        key: "Infogroup",
        icon: undefined,
      });
      menuItems.push({
        icon: <BarChartOutlined />,
        url: "/statistics",
        key: "statistics",
        label: "Statistics",
      });
    }

    return menuItems;
  }, [hasPermission, hasRole]);

  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState(items.find((_item) => _item.url && location.pathname.startsWith(_item.url))?.key ?? "");

  useEffect(() => {
    if (currentClient.id > 0) setSelectedExportClient(currentClient);
  }, [currentClient]);

  useEffect(() => {
    setSelectedKey(items.find((_item) => _item.url && location.pathname.startsWith(_item.url))?.key ?? "");
  }, [items, location]);

  const onClientChange = (value: any, option: any) => {
    const client = availableClients.find((x) => x.id === value);
    if (client) setCurrentClient(client);
  };

  /*const onExportClientChange = (value: any, option: any) => {
    setSelectedExportClient(availableClients.find((x) => x.id === value));
  };

  const exportClientAssets = useCallback(() => {
    const token = localStorage.getItem("accessToken");

    fetch(`${BASE_PATH}/Export/client/assets/${selectedExportClient?.id}`, {
      method: "get",
      headers: {
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => {
        return response.blob();
      })
      .then((blob) => {
        saveAs(blob, "export.xlsx");
      });
  }, [selectedExportClient?.id]);*/

  const siderStyle: React.CSSProperties = {
    overflow: "auto",
    height: "calc(100vh - 64px)",
    position: "sticky",
    top: 64,
    bottom: 0,
    backgroundColor: "#FFF"
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}>
      <Layout style={{ minHeight: "100vh" }} id="adminapp">
        <Header
          style={{
            padding: "0px 24px",
            position: "sticky",
            top: 0,
            width: "100%",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div>
            <Avatar size="large" shape="square" style={{ backgroundColor: "#FFF" }} src="/logo192.png" />
            <span>Administration</span>
          </div>
          {hasRole("Cadesign") && (
            <div>
              <Select
                style={{ width: "200px" }}
                options={availableClients.map((x) => {
                  return { label: x.name, value: x.id };
                })}
                value={selectedExportClient?.id}
                onChange={onClientChange}
                placeholder="Client"></Select>
              <Dropdown
                menu={{
                  items: [
                    {
                      label: "Toggle dark mode",
                      key: "1",
                      icon: <BulbOutlined />,
                      onClick: handleToogleDarkMode,
                    },
                    {
                      label: "Logout",
                      key: "2",
                      icon: <CloseCircleOutlined />,
                      onClick: logOut,
                    },
                  ],
                }}
                placement="bottomRight"
                arrow={{ pointAtCenter: false }}>
                <Button type="text" style={{ color: "#FFF" }} icon={<UserOutlined />} className="user-btn">
                  {username}
                </Button>
              </Dropdown>
            </div>
          )}
        </Header>
        <Layout hasSider>
          <Sider width={200} style={siderStyle}>
            <Menu
              mode="inline"
              items={items.map((e) =>
                e.type === "group"
                  ? {
                    key: e.key,
                    type: e.type,
                    children: [],
                    label: e.label,
                  }
                  : {
                    key: e.key,
                    icon: e.icon,
                    label: <Link to={e.url ?? ""}>{e.label}</Link>
                  }
              )}
              selectedKeys={[selectedKey]}
              style={{ height: "100%", borderRight: 0 }}
            />
          </Sider>
          <Layout style={{ alignSelf: "flex-start" }}>
            {/* <ImportClientAssets clientId={selectedExportClient?.id ?? 0} isOpen={importOpen} onClose={() => setImportOpen(false)} />
              <ExportKingfisherProjects isOpen={exportProjectsOpen} onClose={() => setExportProjectsOpen(false)} />
              <Space>
                <Select
                  style={{ width: "200px" }}
                  options={availableClients.map((x) => {
                    return { label: x.name, value: x.id };
                  })}
                  value={selectedExportClient?.id}
                  onChange={onExportClientChange}></Select>
                <Dropdown
                  menu={{
                    items: [
                      {
                        label: "Export client assets as excel",
                        key: "1",
                        icon: <TableOutlined />,
                        onClick: exportClientAssets,
                      },
                      {
                        label: "Import client assets from excel",
                        key: "2",
                        icon: <TableOutlined />,
                        onClick: () => setImportOpen(true),
                      },
                      {
                        label: "Kingfisher project overview",
                        key: "3",
                        icon: <TableOutlined />,
                        onClick: () => setExportProjectsOpen(true),
                      },
                    ],
                  }}
                  placement="bottomRight"
                  arrow={{ pointAtCenter: false }}>
                  <Button>
                    <ToolOutlined /> Tools
                  </Button>
                </Dropdown>               
              </Space> */}

            <Layout style={{ padding: 24 }}>
              <Content
                style={{
                  padding: 24,
                  margin: 0,
                  minHeight: 280,
                  backgroundColor: colorBgContainer,
                  borderRadius: borderRadiusLG,
                }}>
                <Outlet />
              </Content>
            </Layout>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default AdminApp;
